import { reactive, watch } from 'vue';

export interface FormControlError {
  msg: string;
  meta?: any;
}
export type FormControlValidator = (val: any) => {
  valid: boolean;
  errors?: FormControlError[];
};
export type AsyncFormControlValidator = (
  val: any,
  signal?: AbortSignal
) => Promise<{ valid: boolean; errors?: FormControlError[] }>;
export type FormControlState = 'VALID' | 'VALIDATING' | 'INVALID';
export interface FormControlOptions {
  validators?: FormControlValidator[];
  asyncValidators?: AsyncFormControlValidator[];
  getIsEditing?: () => boolean;
  validateIfTouched?: boolean;
}
export interface FormControl {
  value: any;
  touched: boolean;
  dirty: boolean;
  valid: boolean;
  state: FormControlState;
  errors: FormControlError[];
  updateValidity: () => void;
  reset: () => void;
}

export type FormControlSourceGetter = () =>
  | string
  | number
  | undefined
  | Date
  | boolean
  | Array<string>
  | Array<number>;

export function useFormControl(getSource: FormControlSourceGetter, opts?: FormControlOptions) {
  let initialValue = getSource();
  let asyncValidatorId = 0;
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  let abortController: AbortController | null = null;
  const control = reactive<FormControl>({
    value: initialValue,
    touched: false,
    dirty: false,
    valid: true,
    state: 'VALID',
    errors: [],
    updateValidity: () => forceUpdateValidity(),
    reset: () => init(),
  });

  function forceUpdateValidity() {
    onChange(control.value);
  }

  function init() {
    initialValue = getSource();
    control.value = initialValue;
    control.touched = false;
    control.dirty = false;
    control.valid = true;
    control.state = 'VALID';
    control.errors = [];
  }

  watch(getSource, () => init());
  watch(
    () => (opts?.getIsEditing ? opts?.getIsEditing() : false),
    (val) => {
      if (!val) {
        init();
      }
    }
  );
  watch(
    () => control.state,
    (state) => (control.valid = state === 'VALID')
  );

  function performSyncValidation(val: any) {
    let syncValid = true;
    const syncErrors: FormControlError[] = [];
    opts?.validators?.forEach((v) => {
      const res = v(val);
      syncValid = syncValid && res.valid;
      if (res.errors?.length) {
        syncErrors.push(...res.errors);
      }
    });
    control.state = syncValid ? 'VALID' : 'INVALID';
    control.errors = syncErrors;
  }

  async function performAsyncValidation(val: any, id: number) {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const signal = abortController.signal;
    let asyncValid = true;
    const asyncErrors: FormControlError[] = [];
    for (const v of opts?.asyncValidators || []) {
      try {
        const res = await v(val, signal);
        asyncValid = asyncValid && res.valid;
        if (res.errors) {
          asyncErrors.push(...res.errors);
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        throw err;
      }
    }
    if (asyncValidatorId === id) {
      control.state = asyncValid ? 'VALID' : 'INVALID';
      control.errors = asyncErrors;
    }
  }

  function hasChanges(val: any) {
    if (Array.isArray(val) && arraysEqual(val, initialValue || ([] as any))) {
      return false;
    }
    if (val === initialValue) {
      return false;
    }
    if (typeof val === 'boolean' && !val && !initialValue) {
      return false;
    }
    if (
      (val === undefined || val === null || val === '') &&
      (initialValue === undefined || initialValue === null || initialValue === '')
    ) {
      return false;
    }
    return true;
  }

  function onChange(val: any) {
    control.touched = true;
    const hasChanged = hasChanges(val);
    if (!hasChanged) {
      control.dirty = false;
      control.state = 'VALID';
      control.errors = [];
    } else {
      control.dirty = true;
    }

    if (hasChanged || opts?.validateIfTouched) {
      if(opts?.validators?.length){
        performSyncValidation(val);
      }
      if ((control.state === 'VALID' || !opts?.validators?.length) && opts?.asyncValidators?.length) {
        asyncValidatorId++;
        control.state = 'VALIDATING';
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        debounceTimeout = setTimeout(() => {
          performAsyncValidation(val, asyncValidatorId);
        }, 300);
      }
    }
  }

  watch(
    () => control.value,
    (val) => onChange(val)
  );

  return control;
}

function arraysEqual(a: Array<string | number>, b: Array<string | number>): boolean {
  if (a?.length !== b?.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  for (let i = 0; i < b.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
